import type { CustomPropertyOut } from '@repo/api-codegen';
import { useListCustomProperties } from '@repo/api-codegen';
import type {
	FilterDropdownConfigDate,
	FilterDropdownConfigList,
	FilterDropdownConfigString,
	FilterOption,
	FilterOptionType,
} from '@repo/common/components/Filter';
import {
	FilterDropdownType,
	FilterOperator,
	FilterType,
} from '@repo/common/components/Filter';
import { reduce } from 'lodash-es';

const getDropdownConfig = (
	prop: CustomPropertyOut
):
	| FilterDropdownConfigList
	| FilterDropdownConfigString
	| FilterDropdownConfigDate => {
	switch (prop.value_type) {
		case 'boolean':
			return {
				dropdownType: FilterDropdownType.List,
				defaultOperator: FilterOperator.Is,
				filterType: FilterType.Single,
				getItems: [
					{ label: 'True', value: true },
					{ label: 'False', value: false },
				],
			} as FilterDropdownConfigList;

		case 'user':
		case 'tag':
		case 'resource':
		case 'select':
		case 'multiselect':
			return {
				dropdownType: FilterDropdownType.List,
				defaultOperator: FilterOperator.Is,
				filterType:
					prop.value_type === 'multiselect'
						? FilterType.Multiple
						: FilterType.Single,
				getItems: async () => [],
			} as FilterDropdownConfigList;

		case 'date':
			return {
				dropdownType: FilterDropdownType.Date,
				defaultOperator: FilterOperator.Is,
			} as FilterDropdownConfigDate;

		case 'number':
			return {
				dropdownType: FilterDropdownType.String,
				defaultOperator: FilterOperator.Is,
				inputPlaceholder: `Enter ${prop.name}`,
			} as FilterDropdownConfigString;

		case 'text':
		default:
			return {
				dropdownType: FilterDropdownType.String,
				defaultOperator: FilterOperator.Contains,
				inputPlaceholder: `Enter ${prop.name}`,
			} as FilterDropdownConfigString;
	}
};

export function useCustomFilterOptionsConfig() {
	const { data: customProperties } = useListCustomProperties({});

	const config = reduce(
		customProperties ?? [],
		(acc, customProperty) => {
			const filterOption =
				`custom_property_${customProperty.id}` as FilterOptionType;
			acc[filterOption] = {
				label: customProperty.name,
				type: filterOption,
				field: `properties2.${customProperty.id}`,
				filterDropdownConfig: {
					...getDropdownConfig(customProperty),
					hasIsNotSetOption: true,
				},
			};
			return acc;
		},
		{} as Record<FilterOptionType, FilterOption>
	);

	return config;
}
